import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Divider,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAdjustments,
  IconArrowsExchange,
  IconAlertCircle,
  IconChevronLeft,
  IconChevronRight,
  IconInfoCircle,
  IconRefresh,
} from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import { LedgerEntryModal } from './LedgerEntryModal';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { useBalanceTypes, useUoms } from '../lookups/useLookups';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listItems } from '../items/api';
import { listLocations } from '../locations/api';
import {
  LEDGER_PAGE_SIZE,
  getBalance,
  queryLedger,
  rebuildBalance,
  type BalanceDto,
  type ItemSummary,
  type LedgerEntryDto,
} from './api';

export function InventoryRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const navigate = useNavigate();


  // The header names it. Every endpoint on this screen is warehouse-scoped, and a picker here
  // could disagree with the one the menu and the permission check used.
  const warehouseId = useCurrentWarehouseId();
  const [itemId, setItemId] = useState<string>('');
  const [page, setPage] = useState(0);

  const uoms = useUoms();
  const balanceTypes = useBalanceTypes();

  const items = useQuery({
    queryKey: ['items', 'all'] as const,
    queryFn: ({ signal }) => listItems(true, signal),
  });

  const locations = useQuery({
    queryKey: ['locations', warehouseId || 'none'] as const,
    queryFn: ({ signal }) => listLocations({ warehouseId, includeInactive: true }, signal),
    enabled: Boolean(warehouseId),
  });

  const balance = useQuery({
    queryKey: ['inventory', 'balance', warehouseId, itemId] as const,
    queryFn: ({ signal }) => getBalance(warehouseId, itemId, signal),
    enabled: Boolean(warehouseId && itemId),
  });

  const ledger = useQuery({
    queryKey: ['inventory', 'ledger', warehouseId, itemId || 'all', page] as const,
    queryFn: ({ signal }) =>
      queryLedger(
        {
          warehouseId,
          itemId: itemId || undefined,
          skip: page * LEDGER_PAGE_SIZE,
          take: LEDGER_PAGE_SIZE,
        },
        signal,
      ),
    enabled: Boolean(warehouseId),
  });

  // Reset paging whenever the slice being viewed changes.
  useEffect(() => {
    setPage(0);
  }, [warehouseId, itemId]);

  const itemById = useMemo(() => {
    const map = new Map<string, ItemSummary>();
    for (const i of items.data ?? []) {
      map.set(i.itemId, {
        itemId: i.itemId,
        itemNumber: i.itemNumber,
        description: i.description,
        primaryUomId: i.primaryUomId,
      });
    }
    return map;
  }, [items.data]);

  const locationLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locations.data ?? []) map.set(l.locationId, l.locationCode);
    return map;
  }, [locations.data]);

  const uomLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of uoms.data ?? []) map.set(u.uomId, u.uomCode);
    return map;
  }, [uoms.data]);

  const balanceTypeLabel = useMemo(() => {
    const map = new Map<number, string>();
    for (const bt of balanceTypes.data ?? []) map.set(bt.balanceTypeId, bt.balanceTypeCode);
    return map;
  }, [balanceTypes.data]);

  const selectedItem = itemId ? itemById.get(itemId) : undefined;

  const rebuild = useMutation({
    mutationFn: () => rebuildBalance(warehouseId, itemId),
    onSuccess: () => {
      notifications.show({ message: t('inventory.rebuilt'), color: 'green' });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: () => {
      notifications.show({ message: t('inventory.errors.rebuildFailed'), color: 'red' });
    },
  });

  const itemOptions = useMemo(
    () =>
      (items.data ?? [])
        .filter((i) => i.isActive)
        .map((i) => ({ value: i.itemId, label: `${i.itemNumber} — ${i.description}` })),
    [items.data],
  );

  const canAdjust = permissions.has('inventory:adjust');
  const canMove = permissions.has('inventory:move');
  const canRebuild = permissions.has('tenant:manage_settings');

  function formatQty(value: number): string {
    // Quantities are decimal(19,4); trim trailing zeros so 5.0000 reads as "5".
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(value);
  }

  /**
   * A LEDGER, not a list of things — so the card reads as an entry: the timestamp is the headline,
   * because that is what orders the story, and the signed quantity is the aside, because it is the
   * number somebody is scanning down the page for.
   *
   * The item column stays conditional: on an item-scoped view every row would repeat the same
   * number. Reference is dropped on a phone — it is frequently blank, and a column of dashes is not
   * worth the height.
   */
  /** The row whose full detail is open. A ledger row holds more than a grid can carry. */
  const [viewing, setViewing] = useState<LedgerEntryDto | null>(null);

  const columns: DataColumn<LedgerEntryDto>[] = useMemo(() => {
    // Annotated on the ARRAY, not only on the const: an array literal followed by
    // .filter() gets no contextual type from the outer annotation, so every `align` and
    // `card` widens to string and null stays in the element union.
    const all: (DataColumn<LedgerEntryDto> | null)[] = [
      {
        key: 'when',
        label: t('inventory.when'),
        card: 'title',
        render: (e: LedgerEntryDto) => (
          <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
            {new Date(e.performedAtUtc).toLocaleString()}
          </Text>
        ),
      },
      {
        key: 'tranCode',
        label: t('inventory.tranCode'),
        card: 'subtitle',
        render: (e: LedgerEntryDto) => (
          <Group gap={6} wrap="nowrap">
            <Code>{e.tranCode}</Code>
            {e.isBeginningBalance && (
              <Tooltip label={t('inventory.beginningBalanceHint')}>
                <Badge size="sm" variant="light" color="gray">
                  {t('inventory.beginningBalance')}
                </Badge>
              </Tooltip>
            )}
          </Group>
        ),
      },
      {
        key: 'quantity',
        label: t('inventory.quantity'),
        align: 'right',
        card: 'aside',
        render: (e: LedgerEntryDto) => {
          const negative = e.quantityDelta < 0;
          return (
            <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              <Text component="span" size="sm" fw={500} c={negative ? 'red.7' : 'teal.7'}>
                {negative ? '' : '+'}
                {formatQty(e.quantityDelta)}
              </Text>{' '}
              <Text component="span" size="xs" c="dimmed">
                {uomLabel.get(e.uomId) ?? ''}
              </Text>
            </span>
          );
        },
      },
      itemId
        ? null
        : {
            key: 'item',
            label: t('inventory.item'),
            render: (e: LedgerEntryDto) => {
              const item = itemById.get(e.itemId);
              return item ? (
                <Tooltip label={item.description}>
                  <Code>{item.itemNumber}</Code>
                </Tooltip>
              ) : (
                '—'
              );
            },
          },
      {
        key: 'location',
        label: t('inventory.location'),
        render: (e: LedgerEntryDto) => locationLabel.get(e.locationId) ?? '—',
      },
      {
        key: 'balanceType',
        label: t('inventory.balanceType'),
        render: (e: LedgerEntryDto) => balanceTypeLabel.get(e.balanceTypeId) ?? e.balanceTypeId,
      },
      {
        key: 'reason',
        label: t('inventory.reason'),
        render: (e: LedgerEntryDto) => (
          <Text size="sm" lineClamp={2}>
            {e.reason ?? '—'}
          </Text>
        ),
      },
      {
        key: 'postedBy',
        label: t('inventory.postedBy'),
        render: (e: LedgerEntryDto) =>
          e.performedByName ? (
            <Text size="sm">{e.performedByName}</Text>
          ) : (
            // Not a blank. "A background job did this" and "the screen forgot to tell you" are
            // different answers, and only one of them is a reason to stop looking.
            <Text size="sm" c="dimmed" fs="italic">
              {t('inventory.entry.unattributed')}
            </Text>
          ),
      },
      {
        key: 'reference',
        label: t('inventory.reference'),
        card: 'hidden',
        render: (e: LedgerEntryDto) =>
          e.referenceType || e.referenceId ? (
            <Text size="xs" c="dimmed">
              {[e.referenceType, e.referenceId].filter(Boolean).join(' ')}
            </Text>
          ) : (
            '—'
          ),
      },
    ];
    return all.filter((c): c is DataColumn<LedgerEntryDto> => c !== null);
  },
    [t, itemId, itemById, locationLabel, balanceTypeLabel, uomLabel],
  );

  return (
    <>
      <PageHeader
        title={t('nav.inventory')}
        actions={
          <Group gap="xs">
            {canRebuild && itemId && (
              <Tooltip label={t('inventory.rebuildHint')}>
                <Button
                  variant="default"
                  leftSection={<IconRefresh size={16} />}
                  loading={rebuild.isPending}
                  onClick={() => rebuild.mutate()}
                >
                  {t('inventory.rebuild')}
                </Button>
              </Tooltip>
            )}
            {canMove && (
              <Button
                variant="default"
                leftSection={<IconArrowsExchange size={16} />}
                onClick={() =>
                  void navigate({
                    to: '/app/inventory/move',
                    search: { itemNumber: selectedItem?.itemNumber },
                  })
                }
                disabled={!warehouseId || !selectedItem}
              >
                {t('inventory.move.action')}
              </Button>
            )}
            {/* Goes to the adjustment screen rather than opening a dialog here. That screen is
                also on the menu, and it is the same screen either way — this button just carries
                the item across so the operator does not rescan it. */}
            {canAdjust && (
              <Button
                color="invetrak"
                leftSection={<IconAdjustments size={16} />}
                onClick={() =>
                  void navigate({
                    to: '/app/inventory/adjust',
                    search: { itemNumber: selectedItem?.itemNumber, warehouseId },
                  })
                }
                disabled={!warehouseId || !selectedItem}
              >
                {t('inventory.adjust')}
              </Button>
            )}
          </Group>
        }
      />

      <Stack gap="md">
          <Group gap="sm" wrap="wrap">
            <Select
              label={t('inventory.item')}
              placeholder={t('inventory.itemPlaceholder')}
              description={t('inventory.itemHint')}
              data={itemOptions}
              value={itemId || null}
              onChange={(v) => setItemId(v ?? '')}
              searchable
              clearable
              nothingFoundMessage={t('shell.noMatch')}
              disabled={items.isLoading}
              w={{ base: '100%', sm: 360 }}
            />
          </Group>

          {/* Balance only exists per (warehouse, item), so it appears once an
              item is chosen. The ledger below works either way. */}
          {itemId && (
            <BalanceCard
              query={balance}
              formatQty={formatQty}
              t={t}
            />
          )}

          <Card withBorder radius="md" padding={0}>
            <Group justify="space-between" px="md" py="sm">
              <Text fw={600} size="sm">
                {itemId ? t('inventory.ledgerForItem') : t('inventory.ledgerAll')}
              </Text>
              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {t('inventory.pageLabel', { page: page + 1 })}
                </Text>
                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconChevronLeft size={14} />}
                  disabled={page === 0 || ledger.isFetching}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  {t('inventory.prev')}
                </Button>
                <Button
                  size="xs"
                  variant="default"
                  rightSection={<IconChevronRight size={14} />}
                  // The API returns a page, not a total, so "there may be more"
                  // is inferred from a full page.
                  disabled={(ledger.data?.length ?? 0) < LEDGER_PAGE_SIZE || ledger.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('inventory.next')}
                </Button>
              </Group>
            </Group>
            <Divider />

            {ledger.isLoading && (
              <Center py="xl">
                <Loader color="invetrak" />
              </Center>
            )}

            {ledger.isError && (
              <Alert
                m="md"
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                title={t('inventory.errors.ledgerFailed')}
              >
                <Stack gap="xs">
                  <Text size="sm">{(ledger.error as Error).message}</Text>
                  {ledger.error instanceof ApiError && ledger.error.problem.correlationId && (
                    <Text size="xs" c="dimmed">
                      {t('errors.genericBody')}{' '}
                      <Code>{ledger.error.problem.correlationId}</Code>
                    </Text>
                  )}
                  <Group>
                    <Button size="xs" variant="default" onClick={() => void ledger.refetch()}>
                      {t('inventory.retry')}
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            )}

            {ledger.isSuccess && ledger.data.length === 0 && (
              <Center py="xl">
                <Text c="dimmed" size="sm">
                  {page === 0 ? t('inventory.ledgerEmpty') : t('inventory.ledgerNoMore')}
                </Text>
              </Center>
            )}

            {ledger.isSuccess && ledger.data.length > 0 && (
              <ResponsiveTable
                rows={ledger.data}
                columns={columns}
                rowKey={(e) => e.inventoryTransactionId}
                minWidth={1040}
                actionsWidth={56}
                highlightOnHover
                verticalSpacing="sm"
                horizontalSpacing="md"
                actions={(e) => (
                  <Tooltip label={t('inventory.entry.open')}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={t('inventory.entry.openAria', {
                        when: new Date(e.performedAtUtc).toLocaleString(),
                      })}
                      onClick={() => setViewing(e)}
                    >
                      <IconInfoCircle size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              />
            )}
          </Card>
      </Stack>

      <LedgerEntryModal
        entry={viewing}
        itemLabel={
          viewing
            ? (() => {
                const item = itemById.get(viewing.itemId);
                return item ? `${item.itemNumber} — ${item.description}` : '\u2014';
              })()
            : ''
        }
        locationLabel={viewing ? (locationLabel.get(viewing.locationId) ?? '\u2014') : ''}
        balanceTypeLabel={
          viewing ? (balanceTypeLabel.get(viewing.balanceTypeId) ?? String(viewing.balanceTypeId)) : ''
        }
        uomLabel={viewing ? (uomLabel.get(viewing.uomId) ?? '') : ''}
        onClose={() => setViewing(null)}
      />
    </>
  );
}

/**
 * The balance buckets, grouped so they can be read rather than counted: what is
 * physically in the warehouse, then what is committed against it or expected.
 */
function BalanceCard({
  query,
  formatQty,
  t,
}: {
  query: { isLoading: boolean; isError: boolean; data?: BalanceDto };
  formatQty: (n: number) => string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (query.isLoading) {
    return (
      <Card withBorder radius="md" padding="lg">
        <Center>
          <Loader size="sm" color="invetrak" />
        </Center>
      </Card>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        {t('inventory.errors.balanceFailed')}
      </Alert>
    );
  }

  const b = query.data;
  const physical: [string, number][] = [
    ['inventory.qtyOnHand', b.qtyOnHand],
    ['inventory.qtyQc', b.qtyQc],
    ['inventory.qtyMrb', b.qtyMrb],
    ['inventory.qtyStaging', b.qtyStaging],
    ['inventory.qtyWip', b.qtyWip],
    ['inventory.qtyReceiving', b.qtyReceiving],
    ['inventory.qtyInTransit', b.qtyInTransit],
  ];
  const commitments: [string, number][] = [
    ['inventory.allocated', b.allocated],
    ['inventory.reserved', b.reserved],
    ['inventory.onOrder', b.onOrder],
    ['inventory.backOrdered', b.backOrdered],
  ];

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="md">
        {/* The two numbers an operator actually acts on go first and bigger. */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <Headline label={t('inventory.availableToPromise')} value={formatQty(b.availableToPromise)} />
          <Headline label={t('inventory.availableToReserve')} value={formatQty(b.availableToReserve)} />
          <Headline label={t('inventory.qtyOnHand')} value={formatQty(b.qtyOnHand)} />
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {t('inventory.lastMovement')}
            </Text>
            <Text size="sm">
              {/* MinValue is what the API sends when nothing was ever posted. */}
              {new Date(b.lastModifiedUtc).getFullYear() < 1900
                ? t('inventory.never')
                : new Date(b.lastModifiedUtc).toLocaleString()}
            </Text>
          </Stack>
        </SimpleGrid>

        <Divider label={t('inventory.physicalSection')} labelPosition="left" />
        <SimpleGrid cols={{ base: 2, sm: 4, lg: 7 }} spacing="sm">
          {physical.map(([key, value]) => (
            <Bucket key={key} label={t(key)} value={formatQty(value)} />
          ))}
        </SimpleGrid>

        <Divider label={t('inventory.commitmentsSection')} labelPosition="left" />
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          {commitments.map(([key, value]) => (
            <Bucket key={key} label={t(key)} value={formatQty(value)} />
          ))}
        </SimpleGrid>
      </Stack>
    </Card>
  );
}

function Headline({ label, value }: { label: string; value: string }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="xl" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
    </Stack>
  );
}

function Bucket({ label, value }: { label: string; value: string }) {
  return (
    <Paper withBorder radius="sm" p="xs">
      <Text size="xs" c="dimmed" lineClamp={1}>
        {label}
      </Text>
      <Text size="sm" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
    </Paper>
  );
}
